import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const qualityFeatures = [
  {
    icon: 'ri-home-gear-fill',
    title: 'U.S.-Based Distribution',
    desc: 'OnPoint USA operates exclusively through U.S.-based distribution partners, ensuring strict adherence to domestic quality standards, regulatory compliance, and consistent, reliable product delivery.',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'FDA Compliance Ready',
    desc: 'Our distribution partners maintain robust FDA compliance frameworks, including proper storage, chain-of-custody documentation, and full traceability for every regenerative medicine product we distribute.',
  },
  {
    icon: 'ri-test-tube-fill',
    title: 'USP 797 Sterility Standards',
    desc: 'We work exclusively with facilities that conform to USP 797 standards, ensuring the highest safety and quality for biological products by preventing contamination and preserving product integrity.',
  },
  {
    icon: 'ri-award-fill',
    title: 'WHO/GMP & ISO 9001:2015',
    desc: 'Our partners are WHO/GMP and ISO 9001:2015 certified. These credentials minimize cross-contamination risks, drive process optimization, and ensure internationally accepted quality results.',
  },
  {
    icon: 'ri-global-fill',
    title: 'Global Sourcing for Excellence',
    desc: 'While focused on domestic compliance, we source premium regenerative medicine biologics from globally recognized, FDA-registered facilities to ensure top-tier safety and unmatched quality.',
  },
  {
    icon: 'ri-price-tag-3-fill',
    title: 'Competitive Pricing',
    desc: 'We offer tailored competitive pricing for distributors, healthcare providers, and clinics seeking raw biologics — delivering high-quality regenerative products that meet your budget and business goals.',
  },
  {
    icon: 'ri-medicine-bottle-fill',
    title: 'Clinical-Grade Biologics',
    desc: 'Every product is backed by third-party Certificates of Analysis (COAs), endotoxin testing, and sterility testing — ensuring full transparency, safety, and product purity you can trust.',
  },
  {
    icon: 'ri-truck-fill',
    title: 'Nationwide Reach',
    desc: 'With a robust logistics network and strong industry connections built over 10 years, we can deliver to all 50 states and key international markets — on time, every time.',
  },
  {
    icon: 'ri-links-fill',
    title: 'Extensive Industry Connections',
    desc: 'Our 10-year track record in Regenerative Medicine means we have deep, established relationships with top-tier manufacturers, clinics, and providers — giving you access to the best in the industry.',
  },
];

const differentiators = [
  'Specialized expertise in Regenerative Medicine distribution',
  'Superior quality biologics with full traceability',
  '10+ years of experience and industry connections',
  'Third-party COAs available on all products',
  'Consistent inventory — no shortage delays',
  'Capability to ship to all 50 states and select international markets',
];

export default function ManufacturingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Hero Banner */}
      <section className="relative h-72 flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20pharmaceutical%20manufacturing%20facility%20with%20stainless%20steel%20equipment%2C%20clean%20room%20environment%2C%20regenerative%20medicine%20biologics%20production%2C%20professional%20industrial%20setting%20with%20soft%20blue%20lighting%20and%20white%20surfaces&width=1440&height=576&seq=mfg-hero-01&orientation=landscape"
          alt="Manufacturing and Distribution"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/70 via-blue-900/60 to-blue-950/70" />
        <div className="relative z-10 text-center px-6">
          <span className="inline-block bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            OnPoint USA
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Manufacturing &amp; Distribution
          </h1>
        </div>
      </section>

      {/* Partner Intro */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=close%20up%20of%20scientist%20handling%20regenerative%20medicine%20biological%20vials%20in%20sterile%20lab%20environment%20with%20high-tech%20laboratory%20equipment%2C%20precise%20medical%20research%20setting%2C%20clean%20white%20background&width=700&height=500&seq=mfg-lab-02&orientation=landscape"
              alt="OnPoint USA Distribution"
              className="rounded-lg w-full h-80 object-cover object-top"
            />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-lg flex items-center justify-center" style={{ background: '#002868' }}>
              <div className="text-center">
                <div className="text-white text-2xl font-bold">10+</div>
                <div className="text-blue-200 text-xs leading-tight">Years<br />Experience</div>
              </div>
            </div>
          </div>

          <div>
            <span className="text-red-600 text-xs font-bold uppercase tracking-widest">Distribution Excellence</span>
            <h2 className="text-3xl font-bold mt-2 mb-5" style={{ color: '#002868', fontFamily: 'Montserrat, sans-serif' }}>
              Our Manufacturing &amp; Distribution Partner
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              In the U.S., the Regenerative Medicine landscape is rapidly evolving, making it essential to work with distribution partners who operate with the highest level of quality and compliance. OnPoint USA partners exclusively with U.S.-based distribution and manufacturing facilities that hold comprehensive credentials.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Our partners maintain WHO/GMP and ISO certifications, ensuring transparency, quality, and safety at every step. With over 10 years of experience and deep industry connections, OnPoint USA sets the benchmark for excellence in Regenerative Medicine distribution.
            </p>
            <div className="flex flex-col gap-3">
              {['WHO/GMP Certified Partner', 'ISO 9001:2015 Compliant', 'USP 797 Sterility Standards', 'Third-Party COA on All Products'].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-red-600" />
                  </div>
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Prepared For The Future */}
      <section className="py-16 px-6" style={{ background: '#f8f9ff' }}>
        <div className="max-w-5xl mx-auto text-center mb-12">
          <span className="text-red-600 text-xs font-bold uppercase tracking-widest">Manufacturing Excellence</span>
          <h2 className="text-3xl font-bold mt-2" style={{ color: '#002868', fontFamily: 'Montserrat, sans-serif' }}>
            Prepared For The Future
          </h2>
          <div className="w-12 h-0.5 bg-red-600 mx-auto mt-4 mb-6" />
          <p className="text-gray-600 text-sm leading-relaxed max-w-3xl mx-auto">
            By proactively exceeding current regulatory expectations, our distribution partner ensures that every product meets or surpasses anticipated future guidelines. This forward-thinking approach gives healthcare providers and distributors the confidence that their products are crafted with the highest levels of safety, purity, and reliability.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed max-w-3xl mx-auto mt-4">
            Through our unparalleled commitment to excellence, OnPoint USA delivers regenerative medicine biologics that set the benchmark for quality and compliance — ensuring our clients always have access to the very best.
          </p>
        </div>

        {/* Quality Features Grid */}
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          {qualityFeatures.map((feat) => (
            <div
              key={feat.title}
              className="rounded-lg p-6 border border-blue-100 bg-white hover:border-red-200 transition-colors"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg mb-4" style={{ background: '#002868' }}>
                <i className={`${feat.icon} text-white text-lg`} />
              </div>
              <h3 className="font-bold text-sm mb-2" style={{ color: '#002868' }}>{feat.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How We Differ */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-xl p-10" style={{ background: '#002868' }}>
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              How OnPoint USA Stands Apart
            </h2>
            <p className="text-blue-200 text-sm mb-8">
              Unlike generic distributors, OnPoint USA brings 10 years of specialized Regenerative Medicine expertise, exclusive industry connections, and an unwavering commitment to quality.
            </p>
            <ul className="grid md:grid-cols-2 gap-4">
              {differentiators.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-check-double-line text-red-400 text-base" />
                  </div>
                  <span className="text-blue-100 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Become A Partner CTA */}
      <section className="py-20 px-6 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=medical%20professionals%20in%20modern%20clinic%20discussing%20regenerative%20medicine%20treatment%20plans%2C%20collaborative%20healthcare%20team%2C%20bright%20professional%20environment%20with%20technology%2C%20blue%20and%20white%20tones&width=1440&height=500&seq=mfg-cta-03&orientation=landscape"
          alt="Become a Partner"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-red-700/85" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Become An OnPoint USA Partner
          </h2>
          <p className="text-red-100 text-sm leading-relaxed max-w-2xl mx-auto mb-8">
            Join our growing network of healthcare providers, clinics, and distributors. With OnPoint USA as your distribution partner, you get access to premium regenerative medicine biologics, competitive pricing, and 10 years of unmatched industry expertise.
          </p>
          <Link
            to="/contact-us"
            className="inline-block bg-white text-red-700 font-bold text-sm px-8 py-3 rounded-md hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Get In Touch Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
